<script setup>
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { computed } from "vue";
import { Bar } from "vue-chartjs";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels
);

const props = defineProps({
  chartData: { type: Object, required: true },
  chartOptions: { type: Object, default: () => ({}) },
  threshold: { type: Number, default: null },
  thresholdColor: { type: String, default: "#535353" },
  thresholdLabel: { type: String, default: "" },
  showDataLabels: { type: Boolean, default: true },
  labelsColor: { type: String, default: "#535353" },
  labelFormatter: { type: Function, default: null }, // Will use dynamic formatter if null
  // Dynamic font size configuration
  maxFontSize: { type: Number, default: 22 },
  minFontSize: { type: Number, default: 10 },
  // Dynamic decimal places configuration
  maxDecimalPlaces: { type: Number, default: 3 },
  minDecimalPlaces: { type: Number, default: 0 },
  unitSuffix: { type: String, default: "" },
});

/**
 * Dynamic label formatter that adjusts decimal precision based on data density
 * More data points = fewer decimal places to avoid visual clutter
 * Fewer data points = more decimal places for precise values
 */
const dynamicLabelFormatter = computed(() => {
  return (value) => {
    // Calculate total number of data points in the chart
    const datasetCount = props.chartData.datasets.length;
    const labelsCount = props.chartData.labels.length;
    const totalDataPoints = datasetCount * labelsCount;

    // Determine decimal places based on data density using configurable range
    const decimalRange = props.maxDecimalPlaces - props.minDecimalPlaces;
    let decimalPlaces;

    if (totalDataPoints <= 3) {
      // Few data points: maximum precision (e.g., 21.076%)
      decimalPlaces = props.maxDecimalPlaces;
    } else if (totalDataPoints <= 6) {
      // Medium data: reduce precision by ~33% (e.g., 21.08%)
      decimalPlaces = Math.max(
        props.minDecimalPlaces,
        props.maxDecimalPlaces - Math.floor(decimalRange * 0.33)
      );
    } else if (totalDataPoints <= 12) {
      // More data: reduce precision by ~67% (e.g., 21.1%)
      decimalPlaces = Math.max(
        props.minDecimalPlaces,
        props.maxDecimalPlaces - Math.floor(decimalRange * 0.67)
      );
    } else {
      // Many data points: minimum precision to avoid clutter (e.g., 21%)
      decimalPlaces = props.minDecimalPlaces;
    }

    return `${value.toFixed(decimalPlaces)}${props.unitSuffix}`;
  };
});

// Use custom formatter if provided, otherwise use dynamic formatter
const actualLabelFormatter = computed(() => {
  return props.labelFormatter || dynamicLabelFormatter.value;
});

const thresholdPlugin = {
  id: "thresholdLine",
  afterDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;
    const { left, right } = chartArea;
    const y = scales.y;
    if (!props.threshold || !y) return;

    const yValue = y.getPixelForValue(props.threshold);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left, yValue);
    ctx.lineTo(right, yValue);
    ctx.lineWidth = 2;
    ctx.strokeStyle = props.thresholdColor;
    ctx.setLineDash([6, 6]);
    ctx.stroke();

    if (props.thresholdLabel) {
      ctx.font = "12px sans-serif";
      ctx.fillStyle = props.thresholdColor;
      ctx.textAlign = "left";
      ctx.fillText(props.thresholdLabel, left + 6, yValue - 6);
    }

    ctx.restore();
  },
};

const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        generateLabels: (chart) => {
          const original =
            ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
          return original.map((label) => ({
            ...label,
            fillStyle: "transparent",
            strokeStyle: "transparent",
            pointStyle: false,
          }));
        },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          // Get the value based on chart orientation
          // For horizontal charts (indexAxis: 'y'), the value is in context.parsed.x
          // For vertical charts (indexAxis: 'x'), the value is in context.parsed.y
          const isHorizontal = context.chart.options.indexAxis === "y";
          const rawValue = isHorizontal ? context.parsed.x : context.parsed.y;

          // Use the same formatter as data labels for consistency
          const formattedValue = actualLabelFormatter.value(rawValue);
          return `${context.dataset.label}: ${formattedValue}`;
        },
      },
    },
    datalabels: {
      display: props.showDataLabels,
      color: props.labelsColor,
      align: "end",
      anchor: "end",
      formatter: actualLabelFormatter.value,
      font: (context) => {
        // Calculate dynamic font size based on number of data points
        const chart = context.chart;
        const datasetCount = chart.data.datasets.length;
        const labelsCount = chart.data.labels.length;
        const totalBars = datasetCount * labelsCount;

        // Dynamic font size calculation using configurable min/max
        const fontRange = props.maxFontSize - props.minFontSize;
        let fontSize;

        if (totalBars <= 3) {
          fontSize = props.maxFontSize; // Maximum font for very few bars
        } else if (totalBars <= 6) {
          fontSize = props.maxFontSize - fontRange * 0.2; // 80% of max
        } else if (totalBars <= 12) {
          fontSize = props.maxFontSize - fontRange * 0.4; // 60% of max
        } else if (totalBars <= 20) {
          fontSize = props.maxFontSize - fontRange * 0.7; // 30% of max
        } else {
          fontSize = props.minFontSize; // Minimum font for many bars
        }

        // Ensure we don't go below minimum or above maximum
        fontSize = Math.max(
          props.minFontSize,
          Math.min(props.maxFontSize, Math.round(fontSize))
        );

        return {
          size: fontSize,
          weight: "bold",
        };
      },
    },
  },
  indexAxis: "y",
  scales: {
    x: { grid: { display: false }, ticks: { display: false } },
    y: { grid: { display: false } },
  },
};

const mergedOptions = computed(() => ({
  ...defaultOptions,
  ...props.chartOptions,
  plugins: {
    ...defaultOptions.plugins,
    ...props.chartOptions.plugins,
    tooltip: {
      ...defaultOptions.plugins.tooltip,
      ...(props.chartOptions.plugins?.tooltip || {}),
      callbacks: {
        ...defaultOptions.plugins.tooltip.callbacks,
        ...(props.chartOptions.plugins?.tooltip?.callbacks || {}),
      },
    },
    datalabels: {
      ...defaultOptions.plugins.datalabels,
      ...(props.chartOptions.plugins?.datalabels || {}),
      // Ensure font and formatter functions are preserved if they exist in defaults
      font:
        props.chartOptions.plugins?.datalabels?.font ||
        defaultOptions.plugins.datalabels.font,
      formatter:
        props.chartOptions.plugins?.datalabels?.formatter ||
        defaultOptions.plugins.datalabels.formatter,
    },
  },
  scales: {
    ...defaultOptions.scales,
    ...props.chartOptions.scales,
  },
}));
</script>

<template>
  <div class="bars-chart-container">
    <Bar
      :data="props.chartData"
      :options="mergedOptions"
      :plugins="[thresholdPlugin, ChartDataLabels]"
    />
  </div>
</template>

<style scoped>
.bars-chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.bars-chart-container :deep(canvas) {
  max-width: 100% !important;
  max-height: 100% !important;
}
</style>
